import React from "react"
import { navigate } from "gatsby"
import { Image } from "@chakra-ui/image"
import { Button } from "@chakra-ui/button"
import { ChevronRightIcon } from "@chakra-ui/icons"
import { Box, Flex, VStack, Center, AspectRatio } from "@chakra-ui/layout"

import Paragraph from "~components/shared/text/paragraph"
import SectionHeading from "~components/shared/text/heading"
import ResponsiveBlock from "~components/shared/layout/responsive-block"
import { CompetitorCopy, FeatureComparison } from "./resources/_copy-template"

const ComparisonFeatureBreakdown: React.FC<{
    copy: CompetitorCopy["features"]
}> = ({ copy }) => {
    return (
        <Box bgGradient="linear(to-b, white, gray.100 95%, gray.50 97%)">
            <ResponsiveBlock pb={16} isWide pt={16}>
                <Center>
                    <Center maxW="964px" flexDir="column" gridGap={4}>
                        <SectionHeading size="2xl" textAlign="center">
                            What can Chckvet offer you?
                        </SectionHeading>
                        <Paragraph textAlign="center">
                            Chckvet strives to assist veterinary practices in
                            all aspects of their business. We're not satisfied
                            with the status quo, or making our clients pay for
                            multiple services just to get the features they
                            need.
                        </Paragraph>
                    </Center>
                </Center>
                {copy.map((feature, index) => (
                    <Feature
                        key={index}
                        feature={feature}
                        isInverted={index % 2 === 1}
                    />
                ))}
            </ResponsiveBlock>
        </Box>
    )
}

interface Props {
    feature: FeatureComparison
    isInverted?: boolean
}

const Feature: React.FC<Props> = ({ feature, isInverted }) => {
    const { name, image, chckup, href } = feature
    const desktopDir = isInverted ? "row-reverse" : "row"

    return (
        <Flex
            gridGap={{ base: 16, md: 32 }}
            my={{ base: 24, md: 32 }}
            alignItems="center"
            flexDir={{ base: "column", md: desktopDir }}
        >
            <VStack flex={3} align="left" spacing={{ base: 12, md: 4 }}>
                <SectionHeading size="xl">{name}</SectionHeading>
                <Box
                    position="relative"
                    w="full"
                    role="group"
                    display={{ base: "inline", md: "none" }}
                >
                    <FeatureImage image={image} isInverted={isInverted} />
                </Box>
                <Paragraph>{chckup.description}</Paragraph>
                {href && (
                    <Box>
                        <Button
                            size="sm"
                            colorScheme="brand"
                            rightIcon={<ChevronRightIcon />}
                            onClick={() => {
                                navigate(href)
                            }}
                        >
                            Learn More
                        </Button>
                    </Box>
                )}
            </VStack>
            <Box
                flex={2}
                w="full"
                position="relative"
                transform={{
                    base: "none",
                    md: `${isInverted ? " translateX(-24px)" : ""}`,
                }}
                role="group"
                display={{ base: "none", md: "inline" }}
            >
                <FeatureImage image={image} isInverted={isInverted} />
            </Box>
        </Flex>
    )
}

function FeatureImage({
    image,
    isInverted,
}: {
    image: string
    isInverted?: boolean
}) {
    return (
        <>
            <Box
                position="absolute"
                top={"-2.5%"}
                left={"-2.5%"}
                right={"-2.5%"}
                bottom={"-2.5%"}
                bgGradient="linear(to-tr, brand.500, brand.400)"
                transition={"all 0.2s ease-in-out"}
                transform={`rotate(${isInverted ? "5deg" : "-5deg"})`}
                rounded="xl"
                _groupHover={{
                    transform: `rotate(${isInverted ? "-5deg" : "5deg"})`,
                }}
            />
            {image && (
                <AspectRatio ratio={4 / 3}>
                    <Box
                        h="full"
                        w="full"
                        bg="white"
                        p={4}
                        boxShadow="md"
                        borderColor="gray.100"
                        borderWidth={1}
                        rounded="lg"
                    >
                        <Image
                            src={image}
                            alt={image}
                            w="full"
                            h="full"
                            position="relative"
                            objectFit="contain"
                        />
                    </Box>
                </AspectRatio>
            )}
        </>
    )
}

export default ComparisonFeatureBreakdown
